import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './InstructorDashboard.css';
import Sidebar from './Sidebar';
import Header from './Header';
import HomePage from './HomePage';
import InstructorCourses from './InstructorCourses';
import InstructorExams from './InstructorExams';
import InstructorStudents from './InstructorStudents';
import InstructorStatistics from './InstructorStatistics';
import ProfilModal from './ProfilModal';
import StudyTopics from './StudyTopics';
import CourseQuestions from './CourseQuestions';
import Assignments from './Assignments';
import { ActiveCourseProvider } from './ActiveCourseContext';
import ActiveCourseBar from './ActiveCourseBar';

function InstructorDashboard({ user, onLogout, isImpersonating = false, onReturnToAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // URL'den sayfa bilgisini al (ör: /instructor/courses -> 'courses')
  const getPageFromUrl = (pathname) => {
    if (pathname.startsWith('/instructor/')) {
      const page = pathname.replace('/instructor/', '');
      if (page === '' || page === 'home') return 'home';
      return page;
    }
    return 'home';
  };

  // localStorage'dan activePage'i yükle, yoksa URL'den al, yoksa 'home' kullan
  const [activePage, setActivePage] = useState(() => {
    const urlPage = getPageFromUrl(location.pathname);
    if (urlPage && urlPage !== 'home') return urlPage;
    const savedPage = localStorage.getItem('instructorActivePage');
    return savedPage || 'home';
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth > 768);
  const lastPathnameRef = useRef(location.pathname);

  // URL değiştiğinde state'i güncelle (tarayıcı geri/ileri butonu için)
  useEffect(() => {
    // Sadece URL gerçekten değiştiyse (tarayıcı butonu ile) state'i güncelle
    if (location.pathname !== lastPathnameRef.current) {
      lastPathnameRef.current = location.pathname;
      const urlPage = getPageFromUrl(location.pathname);
      if (urlPage !== activePage) {
        setActivePage(urlPage);
        localStorage.setItem('instructorActivePage', urlPage);
      }
    }
  }, [location.pathname, activePage]);

  // activePage manuel değiştiğinde (menü tıklaması) URL'i güncelle
  const handlePageChange = (page) => {
    setActivePage(page);
    const pagePath = page === 'home' ? '/instructor/home' : `/instructor/${page}`;
    lastPathnameRef.current = pagePath; // Ref'i güncelle ki useEffect tetiklenmesin
    navigate(pagePath); // replace: false - history'ye ekle (geri butonu için)
    localStorage.setItem('instructorActivePage', page);
    // Mobilde menü öğesine tıklanınca sidebar'ı kapat
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  // Mobil kontrolü
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // Desktop'ta sidebar her zaman açık, mobilde kapalı
      if (!mobile) {
        setIsSidebarOpen(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const renderPageContent = () => {
    switch (activePage) {
      case 'home':
        return <HomePage userType="instructor" onPageChange={handlePageChange} />;
      
      case 'courses':
        return <InstructorCourses instructorId={user?.id} />;
      
      case 'exams':
        return <InstructorExams instructorId={user?.id} />;
      
      case 'students':
        return <InstructorStudents instructorId={user?.id} />;
      
      case 'calisma-konulari':
        return <StudyTopics userType="instructor" />;

      case 'odevler':
        return <Assignments userType="instructor" />;

      case 'sorular':
        return <CourseQuestions userType="instructor" />;

      case 'statistics':
        return <InstructorStatistics instructorId={user?.id} />;
      
      case 'profile':
        return (
          <div className="dashboard-section">
            <h2>Bilgilerim</h2>
            <div className="info-card">
              <div className="info-item">
                <span className="info-label">Akademisyen ID:</span>
                <span className="info-value">{user?.id || '-'}</span>
              </div>
              {user?.email && (
                <div className="info-item">
                  <span className="info-label">E-posta:</span>
                  <span className="info-value">{user.email}</span>
                </div>
              )}
              {user?.name && (
                <div className="info-item">
                  <span className="info-label">Ad Soyad:</span>
                  <span className="info-value">{user.name}</span>
                </div>
              )}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <ActiveCourseProvider instructorId={user?.id} role="instructor">
    <div className="instructor-dashboard">
      <Header
        user={user}
        onLogout={onLogout}
        onProfileClick={() => setIsProfileModalOpen(true)}
        onHomeClick={() => handlePageChange('home')}
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        title="KOSTÜ Sınav Modülü"
      />
      
      <Sidebar
        userType="instructor"
        activePage={activePage}
        onPageChange={handlePageChange}
        user={user}
        isImpersonating={isImpersonating}
        onReturnToAdmin={onReturnToAdmin}
        isOpen={isMobile ? isSidebarOpen : true} // Desktop'ta her zaman açık
        onClose={isMobile ? () => setIsSidebarOpen(false) : undefined} // Desktop'ta kapatma butonu yok
      />
      
      <div className="dashboard-main">
        <div className={`dashboard-content ${activePage === 'home' ? 'full-height' : ''}`}>
          {activePage === 'home' ? (
            renderPageContent()
          ) : (
            <div className="dashboard-container">
              <ActiveCourseBar />
              {renderPageContent()}
            </div>
          )}
        </div>
      </div>

      <ProfilModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        user={user}
        userType="instructor"
      />
    </div>
    </ActiveCourseProvider>
  );
}

export default InstructorDashboard;
